import { useCallback } from "react";
import { type DefaultValues, type FieldValues, type SubmitHandler, useForm } from "react-hook-form";

import { FormCheckBoxList } from "./FormCheckBoxList";
import type { FormCheckBoxListProps } from "./FormCheckBoxList";
import { FormInput } from "./FormInput";
import type { FormInputProps } from "./FormInput";
import { FormSelect } from "./FormSelect";
import type { FormSelectProps } from "./FormSelect";
import { FormTextArea } from "./FormTextArea";
import type { FormTextAreaProps } from "./FormTextArea";

interface UseFormUtilsProps<T extends FieldValues> {
  mutationFn: (data: T) => Promise<unknown>;
  defaultValues?: DefaultValues<T>;
}

export const useFormUtils = <T extends FieldValues>({ mutationFn, defaultValues }: UseFormUtilsProps<T>) => {
  const {
    register,
    handleSubmit,
    formState,
    setValue,
    getValues,
    trigger,
    clearErrors,
    reset,
    watch,
    control,
    unregister,
  } = useForm<T>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultValues ?? undefined,
  });

  const onSubmit: SubmitHandler<T> = async (data) => {
    try {
      await mutationFn(data);
    } catch (err) {
      console.error("Error saving form", err);
    }
  };

  const RegisteredFormInput = useCallback(
    (props: Omit<FormInputProps<T>, "register" | "formState">) => (
      <FormInput<T> register={register} formState={formState} {...props} />
    ),
    [register, formState],
  );

  const RegisteredFormSelected = useCallback(
    (props: Omit<FormSelectProps<T>, "register" | "formState" | "setValue" | "watch" | "trigger" | "clearErrors">) => (
      <FormSelect<T>
        register={register}
        formState={formState}
        setValue={setValue}
        watch={watch}
        trigger={trigger}
        clearErrors={clearErrors}
        {...props}
      />
    ),
    [register, formState, setValue, watch, trigger, clearErrors],
  );

  const RegisteredFormTextArea = useCallback(
    (props: Omit<FormTextAreaProps<T>, "register" | "formState">) => (
      <FormTextArea<T> register={register} formState={formState} {...props} />
    ),
    [register, formState],
  );

  const RegisteredCheckboxList = useCallback(
    (props: Omit<FormCheckBoxListProps<T>, "register" | "formState">) => (
      <FormCheckBoxList<T> register={register} formState={formState} {...props} />
    ),
    [register, formState],
  );

  return {
    register,
    unregister,
    setValue,
    control,
    reset,
    getValues,
    trigger,
    watch,
    formState,
    onSubmit: handleSubmit(onSubmit),
    RegisteredFormInput,
    RegisteredFormSelected,
    RegisteredFormTextArea,
    RegisteredCheckboxList,
  };
};
